/**
 * Color Tools for FlowRide Frontend
 * Provides utilities for color conversion, generation, and manipulation.
 * Handles hex, RGB, HSL conversions and color palette generation.
 */

import { type AtlasConfig, type Color } from "../model/AtlasConfig";
import { VehicleType } from "../model/VehicleType";


/**
 * Converts an RGB color object to a normalized hexadecimal string.
 * @param color RGB color object.
 * @returns Hexadecimal color string in the form `#rrggbb`.
 */
export function colorToHex( color: Color ): string {
    const clamp = ( value: number ) => Math.min( 255, Math.max( 0, Math.round( value ) ) );

    return `#${
        [ color.r, color.g, color.b ].
            map( ( value ) => clamp( value ).toString( 16 ).
                padStart( 2, "0" ) ).
            join( "" )
    }`;
}

/**
 * Normalizes hex-like color strings to `#RRGGBB`/`#RGB` compatible format.
 *
 * @param colorHex Input color string.
 * @returns Color with a leading `#`.
 */
export function toReadableHex( colorHex: string ): string {
    const raw = colorHex.trim();
    if ( raw.startsWith( "#" ) ) {
        return raw;
    }

    return `#${ raw }`;
}

/**
 * FlowRide brand primary green (`#11D39C`) for map accents and ferry route overrides.
 */
export const FLOWRIDE_BRAND_PRIMARY_RGB: Color = {
    "r": 17,
    "g": 211,
    "b": 156,
};

/**
 * App accent RGB (`#11D39C`) used when a hex string cannot be parsed or is missing.
 * Shared with {@link VEHICLE_ROUTE_BLACK_OVERRIDE_FALLBACK}.
 */
export const DEFAULT_PARSED_HEX_FALLBACK_RGB: Color = { ...FLOWRIDE_BRAND_PRIMARY_RGB };

/**
 * Parses `#RGB` or `#RRGGBB` (after {@link toReadableHex}) into RGB channels, or null when invalid.
 */
function parseHexToRgbOrNull( colorHex: string ): Color | null {
    const normalized = toReadableHex( colorHex );
    const shortHexMatch = /^#([0-9a-fA-F]{3})$/;
    const longHexMatch = /^#([0-9a-fA-F]{6})$/;

    if ( shortHexMatch.test( normalized ) ) {
        const matched = normalized.match( shortHexMatch );
        if ( matched == null ) {
            return null;
        }
        const [ r1, g1, b1 ] = matched[1].split( "" );

        return {
            "r": Number.parseInt( `${ r1 }${ r1 }`, 16 ),
            "g": Number.parseInt( `${ g1 }${ g1 }`, 16 ),
            "b": Number.parseInt( `${ b1 }${ b1 }`, 16 ),
        };
    }

    if ( longHexMatch.test( normalized ) ) {
        const matched = normalized.match( longHexMatch );
        if ( matched == null ) {
            return null;
        }
        const value = matched[1];

        return {
            "r": Number.parseInt( value.slice( 0, 2 ), 16 ),
            "g": Number.parseInt( value.slice( 2, 4 ), 16 ),
            "b": Number.parseInt( value.slice( 4, 6 ), 16 ),
        };
    }

    return null;
}

/**
 * Converts a hex string (`#RGB` / `#RRGGBB`, with or without `#`) to RGB.
 * When the input is null, undefined, empty/whitespace-only, or not parseable as hex, returns a copy
 * of `defaultColor` (defaults to {@link DEFAULT_PARSED_HEX_FALLBACK_RGB}).
 *
 * @param colorHex Hex color string.
 * @param defaultColor RGB used when the input is absent or invalid.
 * @returns A new RGB object.
 */
export function hexToRgb(
    colorHex: string | null | undefined,
    defaultColor: Color = DEFAULT_PARSED_HEX_FALLBACK_RGB,
): Color {
    if ( colorHex === null || colorHex === undefined || colorHex.trim() === "" ) {
        return { ...defaultColor };
    }

    const parsed = parseHexToRgbOrNull( colorHex );
    if ( parsed === null ) {
        return { ...defaultColor };
    }

    return { ...parsed };
}

/**
 * Generate a rainbow color palette
 * @param numColors - Number of colors to generate
 * @returns Array of RGB colors
 */
export function generateRainbowColors( numColors: number ): Color[] {
    const colors: Color[] = [];
    for ( let i = 0; i < numColors; i++ ) {
        const hue = ( i * 360 ) / numColors;
        const colorHex = convertHslToHex( hue, 100, 50 );
        colors.push( hexToRgb( colorHex ) );
    }

    return colors;
}

/**
 * Picks readable text color on top of a background color.
 *
 * @param backgroundHex Background color in hex format.
 * @returns Dark or light text color based on luminance heuristic.
 */
export function getBestTextColorOnBackground( backgroundHex: string ): string {
    const rgb = parseHexToRgbOrNull( backgroundHex );
    if ( rgb == null ) {
        return "#ffffff";
    }

    const luminance = ( 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b ) / 255;

    return luminance > 0.6 ? "#0f172a" : "#ffffff";
}

/**
 * Quantizes RGB channels to reduce the number of distinct colors.
 * @param color RGB color object.
 * @param step Quantization step per channel.
 * @returns Quantized RGB color.
 */
export function quantizeColor( color: Color, step: number ): Color {
    const quantize = ( value: number ) => Math.max( 0, Math.min( 255, Math.round( value / step ) * step ) );

    return {
        "r": quantize( color.r ),
        "g": quantize( color.g ),
        "b": quantize( color.b ),
    };
}

/**
 * Replaces black (or near-black) colors with a fallback color.
 * @param color RGB color object.
 * @param threshold Maximum channel value considered black-like.
 * @param fallbackColor Fallback color used when the input is black-like.
 * @returns Original color or fallback color.
 */
export function applyBlackColorOverride( color: Color, threshold: number, fallbackColor: Color ): Color {
    const isBlackLike = color.r <= threshold && color.g <= threshold && color.b <= threshold;
    if ( !isBlackLike ) {
        return color;
    }

    return fallbackColor;
}

/**
 * Threshold (per channel) for treating a GTFS route color as black-like.
 * Matches {@link VehicleCustomLayer} bus texture logic.
 */
export const VEHICLE_ROUTE_BLACK_OVERRIDE_THRESHOLD = 12;

/**
 * Fallback when route color is black-like: app primary green (`#11D39C`).
 * Same channels as {@link DEFAULT_PARSED_HEX_FALLBACK_RGB}.
 */
export const VEHICLE_ROUTE_BLACK_OVERRIDE_FALLBACK: Color = { ...DEFAULT_PARSED_HEX_FALLBACK_RGB };

/**
 * Minimum lead of the blue channel over red/green for GTFS colors treated as "blue line".
 */
export const VEHICLE_ROUTE_BLUE_OVERRIDE_MIN_CHANNEL_GAP = 20;

/**
 * Returns whether a route color reads as blue/cyan (typical ferry GTFS palette).
 *
 * @param color Raw route RGB.
 * @returns True when blue is clearly the dominant channel.
 */
export function isBlueLikeRouteColor( color: Color ): boolean {
    const { r, g, b } = color;

    if (
        r <= VEHICLE_ROUTE_BLACK_OVERRIDE_THRESHOLD &&
        g <= VEHICLE_ROUTE_BLACK_OVERRIDE_THRESHOLD &&
        b <= VEHICLE_ROUTE_BLACK_OVERRIDE_THRESHOLD
    ) {
        return false;
    }

    const maxOther = Math.max( r, g );

    return b > maxOther + VEHICLE_ROUTE_BLUE_OVERRIDE_MIN_CHANNEL_GAP;
}

/**
 * Resolves GTFS route RGB for map lines and markers.
 *
 * @param color Raw route color from realtime / vehicle model.
 * @returns Visible color (green when input was near-black).
 */
export function resolveVehicleRouteDisplayColor( color: Color ): Color {
    return applyBlackColorOverride(
        color,
        VEHICLE_ROUTE_BLACK_OVERRIDE_THRESHOLD,
        VEHICLE_ROUTE_BLACK_OVERRIDE_FALLBACK,
    );
}

/**
 * Resolves GTFS route RGB for vehicle icons.
 * Ferry/watercraft icons that are blue are remapped to FlowRide green so boats stand out visually.
 *
 * @param color Raw route color from realtime / vehicle model.
 * @param vehicleType Vehicle type.
 * @returns Visible icon color.
 */
export function resolveVehicleIconDisplayColor( color: Color, vehicleType: VehicleType ): Color {
    const withoutBlack = resolveVehicleRouteDisplayColor( color );

    if (
        ( vehicleType === VehicleType.Ferry || vehicleType === VehicleType.Water ) &&
        isBlueLikeRouteColor( withoutBlack )
    ) {
        return { ...FLOWRIDE_BRAND_PRIMARY_RGB };
    }

    return withoutBlack;
}

/**
 * Parses a hex color string (#RRGGBB) to RGB, or null when invalid.
 */
function parseHexColorStringToColor( hex: string ): Color | null {
    const trimmed = hex.trim();
    const match = ( /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i ).exec( trimmed );
    if ( match === null ) {
        return null;
    }

    return {
        "r": parseInt( match[1], 16 ),
        "g": parseInt( match[2], 16 ),
        "b": parseInt( match[3], 16 ),
    };
}

/**
 * When a hex color is black-like (e.g. itinerary UNKNOWN mode `#000`), returns the same
 * green as vehicle route overlays; otherwise returns the input hex unchanged.
 *
 * @param hex Color string such as `#000000` or `#E67E22`.
 * @returns Hex string safe for MapLibre paint properties.
 */
export function resolveBlackLikeHexToVehicleRouteGreen( hex: string ): string {
    const rgb = parseHexColorStringToColor( hex );
    if ( rgb === null ) {
        return hex;
    }

    return colorToHex( resolveVehicleRouteDisplayColor( rgb ) );
}

/**
 * Raises very dark colors to a minimum luminance threshold while preserving hue ratio.
 * @param color RGB color object.
 * @param minLuminance Minimum target luminance.
 * @param maxBoost Maximum multiplication factor to avoid over-brightening.
 * @returns Brightened color when needed, otherwise original color.
 */
export function liftDarkColor( color: Color, minLuminance: number, maxBoost: number = 1.8 ): Color {
    const clamp = ( value: number ) => Math.max( 0, Math.min( 255, Math.round( value ) ) );
    const luminance = ( 0.2126 * color.r ) + ( 0.7152 * color.g ) + ( 0.0722 * color.b );
    if ( luminance >= minLuminance ) {
        return color;
    }
    if ( luminance <= 1 ) {
        return {
            "r": minLuminance,
            "g": minLuminance,
            "b": minLuminance,
        };
    }

    const factor = Math.min( maxBoost, minLuminance / luminance );

    return {
        "r": clamp( color.r * factor ),
        "g": clamp( color.g * factor ),
        "b": clamp( color.b * factor ),
    };
}

/**
 * Convert HSL color to hexadecimal (#RRGGBB)
 * @param h - Hue, between 0 and 360
 * @param s - Saturation, between 0 and 100
 * @param l - Lightness, between 0 and 100
 * @returns Hexadecimal color string
 */
function convertHslToHex( h: number, s: number, l: number ): string {
    s /= 100;
    l /= 100;
    const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
    const x = c * ( 1 - Math.abs( ( h / 60 ) % 2 - 1 ) );
    const m = l - c / 2;
    let r = 0;
    let g = 0;
    let b = 0;
    if ( h >= 0 && h < 60 ) {
        r = c;
        g = x;
        b = 0;
    } else if ( h >= 60 && h < 120 ) {
        r = x;
        g = c;
        b = 0;
    } else if ( h >= 120 && h < 180 ) {
        r = 0;
        g = c;
        b = x;
    } else if ( h >= 180 && h < 240 ) {
        r = 0;
        g = x;
        b = c;
    } else if ( h >= 240 && h < 300 ) {
        r = x;
        g = 0;
        b = c;
    } else if ( h >= 300 && h < 360 ) {
        r = c;
        g = 0;
        b = x;
    }
    r = Math.round( ( r + m ) * 255 );
    g = Math.round( ( g + m ) * 255 );
    b = Math.round( ( b + m ) * 255 );

    return `#${ ( ( 1 << 24 ) + ( r << 16 ) + ( g << 8 ) + b ).toString( 16 ).slice( 1 ).
        toUpperCase() }`;
}

/**
 * Trouve la ligne et la colonne correspondant à la couleur la plus proche dans un atlas.
 * @param targetColor La couleur cible en RGB {r, g, b}.
 * @param atlasConfig La configuration de l'atlas avec les couleurs disponibles.
 * @returns Les coordonnées {row, col} de la cellule dans l'atlas.
 */
export function findClosestColorInAtlas( targetColor: Color, atlasConfig: AtlasConfig ): {
    "row": number;
    "col": number;
} {
    const { cols, colors } = atlasConfig;

    let closestIndex = -1;
    let minDistance = Infinity;

    // Parcourir toutes les couleurs de l'atlas
    for ( let i = 0; i < colors.length; i++ ) {
        const color = colors[i];
        const distance = Math.sqrt( Math.pow( color.r - targetColor.r, 2 ) +
          Math.pow( color.g - targetColor.g, 2 ) +
          Math.pow( color.b - targetColor.b, 2 ) );

        if ( distance < minDistance ) {
            minDistance = distance;
            closestIndex = i;
        }
    }

    if ( closestIndex === -1 ) {
        throw new Error( "Aucune couleur trouvée dans l'atlas." );
    }

    // Calculer les coordonnées (row, col) à partir de l'index
    const row = Math.floor( closestIndex / cols );
    const col = closestIndex % cols;

    return {
        row,
        col,
    };
}
